/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { MainFiltersHeader } from 'src/core/components/MainFiltersHeader';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FiltersSection } from 'src/verticals/utterancesFilters/components/FiltersSection';
import { FiltersItem, FilterType, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type PatternsBasicFilterProps = {
    /**
     * Count the number of filtered intents
     */
    resultsCount: number;

    /**
     * Input entities
     */
    entities: FiltersItem[];

    /**
     * Input Intents
     */
    intents: FiltersItem[];

    /**
     * Determines whether at least one filter is applied or not.
     */
    areFiltersApplied: boolean;

    /**
     * Handler function for entities and nearest rival blade
     */
    onOpenBladeClick: (bladeId: FilterType) => void;

    /**
     * Handler function for clear all filters button
     */
    onClearAllClick: () => void;

    /**
     * Handler function for item selection
     */
    onSelectionChange: (change: SelectionChange) => void;
};

const messages = defineMessages({
    mainFiltersBladeHeader: {
        id: 'patterns.basicFilter.bladeHeader',
        defaultMessage: 'Filters'
    },
    entitiesHeader: {
        id: 'patterns.basicFilter.entitiesHeader',
        defaultMessage: 'Entities'
    },
    intentsHeader: {
        id: 'patterns.basicFilter.intentsHeader',
        defaultMessage: 'Intents'
    }
});

/**
 *
 * Represents the basic filters blade for patterns
 */
export const PatternsBasicFilter = (props: PatternsBasicFilterProps) => {
    const { resultsCount, entities, intents, onOpenBladeClick, onClearAllClick, onSelectionChange, areFiltersApplied } = props;
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 24 }}>
            <MainFiltersHeader
                title={localize(messages.mainFiltersBladeHeader)}
                onClearAllClick={onClearAllClick}
                resultsCount={resultsCount}
                areFiltersApplied={areFiltersApplied}
            />

            <FiltersSection
                title={localize(messages.entitiesHeader)}
                onSelectionChange={onSelectionChange}
                items={entities}
                itemsLimit={7}
                onSeeAllBtnClick={() => onOpenBladeClick('entities')}
            />

            <FiltersSection
                title={localize(messages.intentsHeader)}
                onSelectionChange={onSelectionChange}
                items={intents}
                itemsLimit={7}
                onSeeAllBtnClick={() => onOpenBladeClick('intents')}
            />
        </Stack>
    );
};
